// Browse — the Epicure gallery over recipes.db: filter pills, aspect-4/5
// image cards with big radii and skeleton shimmer. Tap → RecipeDetail.

import { useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { Clock, Search } from "lucide-react";

import { fetcher, imageUrl } from "@/api/client";
import { cn } from "@/lib/utils";

interface RecipeCard {
  id: number;
  name: string;
  prep_min: number | null;
  cook_min: number | null;
  cuisine: string | null;
  image: string | null;
  tags: string;
}

export default function Browse() {
  const [query, setQuery] = useState("");
  const [cuisine, setCuisine] = useState("");

  const params = new URLSearchParams();
  if (query) params.set("query", query);
  if (cuisine) params.set("cuisine", cuisine);
  const { data: recipes, isLoading } = useSWR<RecipeCard[]>(
    `/api/recipes?${params.toString()}`,
    fetcher,
  );
  const { data: cuisines } = useSWR<string[]>("/api/recipes/cuisines", fetcher);

  return (
    <div className="flex flex-1 flex-col overflow-y-auto p-4 md:p-6">
      {/* filter row: search pill + cuisine pills */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-w-52 items-center gap-2 rounded-full border border-line-strong bg-surface px-4 py-2">
          <Search className="size-4 shrink-0 text-dim" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="search recipes"
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-dimmer"
          />
        </label>
        {(cuisines ?? []).slice(0, 12).map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCuisine(cuisine === c ? "" : c)}
            className={cn(
              "cursor-pointer rounded-full px-3.5 py-1.5 text-xs font-semibold tracking-[0.01em]",
              "transition-[background-color,color,transform] duration-500 ease-epicure hover:-translate-y-px",
              cuisine === c
                ? "bg-brand text-primary-foreground"
                : "border border-line-strong text-dim hover:text-foreground",
            )}
          >
            {c}
          </button>
        ))}
      </div>

      {/* card grid */}
      {isLoading ? (
        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="epicure-skeleton aspect-[4/5] rounded-[var(--radius-card)]" />
          ))}
        </div>
      ) : recipes && recipes.length > 0 ? (
        <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
          {recipes.map((r) => (
            <Link
              key={r.id}
              to={`/browse/${r.id}`}
              className="group relative block aspect-[4/5] overflow-hidden rounded-[var(--radius-card)] border border-line bg-panel transition-transform duration-700 ease-epicure hover:-translate-y-1"
            >
              {imageUrl(r.image) ? (
                <img
                  src={imageUrl(r.image)!}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-epicure group-hover:scale-[1.03]"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center font-display text-6xl italic text-dimmer">
                  {r.name.slice(0, 1)}
                </div>
              )}
              {/* caption on a bottom gradient — cream text works on any photo */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-3.5 pt-10">
                <div className="font-display text-xl leading-tight font-semibold text-[#f5f2e8]">
                  {r.name}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-[#f5f2e8]/70">
                  {(r.cook_min ?? r.prep_min) != null && (
                    <span className="flex items-center gap-1">
                      <Clock className="size-3" />
                      {(r.prep_min ?? 0) + (r.cook_min ?? 0)} min
                    </span>
                  )}
                  {r.cuisine && <span>{r.cuisine}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center p-10 text-center">
          <div>
            <p className="font-display text-4xl italic text-dim">an empty pantry…</p>
            <p className="mt-3 max-w-sm text-sm text-dim">
              No recipes yet. Like dishes in <b>Discover</b> and they appear here, or ask the
              chef to import one.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
